/**
 * HerbKranti Media / Image Upload — shared frontend utilities.
 *
 * Consumers should only touch <ImageField /> and <ImageGalleryField />.
 * They handle: pick-from-computer, drag-and-drop, preview, replace,
 * remove, upload progress, error surfacing, and turning a raw file into
 * a persistent URL served by the backend.
 *
 * `<ImageField value={url} onChange={setUrl} />` — single image.
 * `<ImageGalleryField items={arr} onChange={setArr} />` — ordered list.
 *
 * Uploaded URLs come back as `/api/media/<id>?v=<epoch>` — the caller can
 * prepend `${REACT_APP_BACKEND_URL}` when rendering, or use the helper
 * `resolveMediaUrl()`.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, X, Image as ImageIcon, Trash2, Move, Star, StarOff, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 10;
const ACCEPTED = "image/jpeg,image/jpg,image/png,image/webp,image/svg+xml,image/gif,image/x-icon,image/vnd.microsoft.icon";

/** Prepend the backend host to relative /api/media/... paths so <img> works. */
export const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
};

const adminHeaders = () => {
  const pw = typeof window !== "undefined" ? sessionStorage.getItem("hk_admin_pw") : null;
  return pw ? { "X-Admin-Password": pw } : {};
};

/** Upload a File to /admin/media/upload. Reports progress via onProgress(0-100). */
export const uploadMedia = async (file, { alt = "", tags = "", onProgress } = {}) => {
  if (!file) throw new Error("No file selected");
  if (file.size > MAX_MB * 1024 * 1024) {
    throw new Error(`File is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Max ${MAX_MB} MB.`);
  }
  const fd = new FormData();
  fd.append("file", file);
  if (alt) fd.append("alt", alt);
  if (tags) fd.append("tags", tags);
  const res = await axios.post(`${API}/admin/media/upload`, fd, {
    headers: { ...adminHeaders() },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return res.data; // {id, url, filename, mime, size, width, height, ...}
};

export const listMedia = async (q = "") => {
  const res = await axios.get(`${API}/admin/media`, { headers: adminHeaders(), params: q ? { q } : {} });
  return res.data;
};

export const deleteMedia = async (id) => {
  const res = await axios.delete(`${API}/admin/media/${id}`, { headers: adminHeaders() });
  return res.data;
};

/**
 * <ImageField>
 * Props:
 *   value      — current image URL (may be legacy external URL or /api/media/... path)
 *   onChange   — (newUrl: string) => void  (empty string on remove)
 *   label      — visible label
 *   aspect     — 'square' | 'wide' | 'tall' | 'auto'
 *   testid     — data-testid prefix
 *   help       — optional helper text
 *   allowUrl   — show "or paste URL" advanced input (default true)
 */
export const ImageField = ({
  value, onChange, label = "Image", aspect = "square", testid = "image", help = "",
  allowUrl = true,
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const inputRef = useRef(null);

  const doUpload = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const r = await uploadMedia(file, { onProgress: setProgress });
      onChange(r.url);
      toast.success("Image uploaded");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const aspectCls = {
    square: "aspect-square",
    wide: "aspect-[16/9]",
    tall: "aspect-[3/4]",
    auto: "min-h-[140px]",
  }[aspect] || "aspect-square";

  const hasImage = !!value;
  const resolved = resolveMediaUrl(value);

  return (
    <div className="space-y-2" data-testid={`${testid}-field`}>
      {label && <label className="text-[11px] tracking-widest uppercase text-[var(--hk-ink-2)]">{label}</label>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={
          "relative rounded-md border overflow-hidden bg-[var(--hk-warm-ivory)] " + aspectCls + " " +
          (drag ? "border-[var(--hk-orange)] ring-2 ring-[var(--hk-orange)]/25" : "border-[var(--hk-border)]")
        }
      >
        {hasImage ? (
          <>
            <img src={resolved} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid={`${testid}-preview`} />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-end justify-end gap-1 p-2 opacity-0 hover:opacity-100">
              <button type="button" onClick={() => inputRef.current?.click()} className="text-[11px] px-2 py-1 rounded bg-white text-[var(--hk-ink)] shadow" data-testid={`${testid}-replace-btn`}>
                <Upload size={11} className="inline mr-1" /> Replace
              </button>
              <button type="button" onClick={() => { if (window.confirm("Remove this image?")) onChange(""); }} className="text-[11px] px-2 py-1 rounded bg-red-500 text-white shadow" data-testid={`${testid}-remove-btn`}>
                <Trash2 size={11} className="inline mr-1" /> Remove
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-[var(--hk-ink-3)] hover:text-[var(--hk-orange-deep)] p-4 text-center"
            data-testid={`${testid}-upload-btn`}
          >
            {uploading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[11px]">Uploading… {progress}%</span>
              </>
            ) : (
              <>
                <ImageIcon size={22} />
                <span className="text-[11px]">Drop or click to upload</span>
                <span className="text-[10px] opacity-70">JPG, PNG, WebP, SVG · up to {MAX_MB} MB</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) doUpload(f);
          e.target.value = "";
        }}
      />
      {allowUrl && (
        <details className="text-[11px]">
          <summary className="text-[var(--hk-ink-3)] cursor-pointer select-none">Advanced: paste image URL</summary>
          <div className="flex gap-2 mt-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://…"
              className="flex-1 h-9 px-2 rounded border border-[var(--hk-border)] bg-white/70 text-[12px]"
              data-testid={`${testid}-url-input`}
            />
            <button
              type="button"
              onClick={() => { if (urlInput.trim()) { onChange(urlInput.trim()); setUrlInput(""); toast.success("URL saved"); } }}
              className="hk-btn-ghost h-9 px-3 text-[11px]"
              data-testid={`${testid}-url-save-btn`}
            >Use</button>
          </div>
        </details>
      )}
      {help && <p className="text-[11px] text-[var(--hk-ink-3)]">{help}</p>}
    </div>
  );
};

/**
 * <ImageGalleryField>
 * Manages an ordered list of images (product gallery style).
 *
 * Props:
 *   items       — Array<string | {url, alt?}>
 *   onChange    — (newItems: Array<{url, alt}>) => void
 *   maxItems    — cap (default 12)
 *   testid      — data-testid prefix
 */
export const ImageGalleryField = ({ items = [], onChange, maxItems = 12, testid = "gallery" }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Normalize items to {url, alt}
  const normalized = items.map((it) => (typeof it === "string" ? { url: it, alt: "" } : { url: it?.url || it?.image || "", alt: it?.alt || "" })).filter((it) => it.url);

  const emit = (arr) => onChange(arr);

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    if (normalized.length + list.length > maxItems) {
      toast.error(`Max ${maxItems} images allowed. Remove some first.`);
      return;
    }
    setUploading(true);
    setProgress(0);
    let done = 0;
    const next = [...normalized];
    for (const file of list) {
      try {
        const r = await uploadMedia(file, { onProgress: (p) => setProgress(Math.round((done * 100 + p) / list.length)) });
        next.push({ url: r.url, alt: "" });
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message || `Failed: ${file.name}`);
      }
      done++;
    }
    emit(next);
    setUploading(false);
    setProgress(0);
    toast.success(`${done} image${done > 1 ? "s" : ""} uploaded`);
  };

  const move = (idx, delta) => {
    const arr = [...normalized];
    const to = idx + delta;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    emit(arr);
  };
  const setPrimary = (idx) => {
    if (idx === 0) return;
    const arr = [...normalized];
    const [chosen] = arr.splice(idx, 1);
    arr.unshift(chosen);
    emit(arr);
  };
  const removeAt = (idx) => {
    if (!window.confirm("Remove this image from the gallery?")) return;
    const arr = normalized.filter((_, i) => i !== idx);
    emit(arr);
  };
  const setAlt = (idx, alt) => {
    const arr = normalized.map((it, i) => (i === idx ? { ...it, alt } : it));
    emit(arr);
  };

  return (
    <div className="space-y-3" data-testid={`${testid}-field`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] tracking-widest uppercase text-[var(--hk-ink-2)]">
          Gallery — {normalized.length}/{maxItems}
        </label>
        <span className="text-[11px] text-[var(--hk-ink-3)]">First image is the primary product image.</span>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
        className={"rounded-md border-2 border-dashed p-4 text-center " + (dragOver ? "border-[var(--hk-orange)] bg-[var(--hk-warm-ivory)]" : "border-[var(--hk-border)] bg-white/60")}
        data-testid={`${testid}-dropzone`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          multiple
          className="hidden"
          data-testid={`${testid}-input`}
          onChange={(e) => {
            uploadFiles(e.target.files);
            e.target.value = "";
          }}
        />
        {uploading ? (
          <div className="text-[12px] text-[var(--hk-ink-2)] flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Uploading… {progress}%
          </div>
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()} className="text-[12px] text-[var(--hk-ink-2)]" data-testid={`${testid}-add-btn`}>
            <Upload size={13} className="inline mr-1" /> Drop images here or click to upload (multi-select supported)
          </button>
        )}
      </div>

      {normalized.length === 0 ? (
        <div className="text-[12px] text-[var(--hk-ink-3)] italic">No images yet.</div>
      ) : (
        <ul className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {normalized.map((it, idx) => (
            <li key={it.url + idx} className="relative rounded-md border border-[var(--hk-border)] overflow-hidden bg-[var(--hk-warm-ivory)]" data-testid={`${testid}-item-${idx}`}>
              <div className="aspect-square relative">
                <img src={resolveMediaUrl(it.url)} alt={it.alt || ""} className="absolute inset-0 w-full h-full object-cover" />
                {idx === 0 && (
                  <span className="absolute top-1 left-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--hk-orange)] text-white uppercase tracking-widest">Primary</span>
                )}
              </div>
              <div className="p-2 space-y-1.5">
                <input
                  value={it.alt}
                  onChange={(e) => setAlt(idx, e.target.value)}
                  placeholder="Alt text (accessibility + SEO)"
                  className="w-full h-7 px-1.5 rounded border border-[var(--hk-border)] bg-white text-[11px]"
                  data-testid={`${testid}-alt-${idx}`}
                />
                <div className="flex items-center justify-between gap-1">
                  <div className="flex gap-1">
                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[10px] px-1.5 py-0.5 border border-[var(--hk-border)] rounded disabled:opacity-40" data-testid={`${testid}-up-${idx}`}>↑</button>
                    <button type="button" onClick={() => move(idx, 1)} disabled={idx === normalized.length - 1} className="text-[10px] px-1.5 py-0.5 border border-[var(--hk-border)] rounded disabled:opacity-40" data-testid={`${testid}-down-${idx}`}>↓</button>
                  </div>
                  <button type="button" onClick={() => setPrimary(idx)} title="Make primary" className="text-[10px] px-1.5 py-0.5 border border-[var(--hk-border)] rounded" data-testid={`${testid}-primary-${idx}`}>
                    {idx === 0 ? <Star size={11} className="fill-current text-[var(--hk-orange)]" /> : <StarOff size={11} />}
                  </button>
                  <button type="button" onClick={() => removeAt(idx)} title="Remove" className="text-[10px] px-1.5 py-0.5 border border-red-200 text-red-600 rounded" data-testid={`${testid}-remove-${idx}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Compact Media Library modal / picker — can be used by any field to reuse existing assets. */
export const MediaPicker = ({ open, onClose, onPick, testid = "picker" }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    (async () => {
      setBusy(true);
      try {
        const r = await listMedia(q);
        setItems(r);
      } catch { toast.error("Failed to load media"); }
      finally { setBusy(false); }
    })();
  }, [open, q]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid={`${testid}-backdrop`}>
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[var(--hk-border)] flex gap-3 items-center">
          <h3 className="serif text-[18px]">Media Library</h3>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="flex-1 h-9 px-3 rounded-md border border-[var(--hk-border)] bg-white text-[13px]" data-testid={`${testid}-search`} />
          <button onClick={onClose} className="hk-btn-ghost h-9 px-3 text-[12px]" data-testid={`${testid}-close`}>
            <X size={14} /> Close
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {busy ? (
            <div className="text-center text-[13px] py-8"><Loader2 size={16} className="animate-spin inline" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-[13px] py-8 text-[var(--hk-ink-3)]">No media yet — upload one from any field to see it here.</div>
          ) : (
            <ul className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {items.map((it) => (
                <li key={it.id} className="cursor-pointer rounded-md border border-[var(--hk-border)] overflow-hidden hover:ring-2 hover:ring-[var(--hk-orange)]" onClick={() => { onPick(it.url); onClose(); }} data-testid={`${testid}-item-${it.id}`}>
                  <div className="aspect-square bg-[var(--hk-warm-ivory)] relative">
                    <img src={resolveMediaUrl(it.url)} alt={it.alt || it.filename} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <div className="p-1.5 text-[10px] text-[var(--hk-ink-3)] truncate">{it.filename}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageField;
